import { useEffect, useState } from 'react';

function sectionHasActive(items, activeKey) {
  return items?.some(i => i.key === activeKey);
}

export default function Sidebar({
  open,
  onClose,
  sections,
  activeKey,
  docked = false,
  state = 'expanded',
  onEdgeToggle,
  onHide,
}) {
  const [collapsed, setCollapsed] = useState({});
  const iconOnly = docked && state === 'collapsed';

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('tmr-nav-collapse') || '{}');
      setCollapsed(saved);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!open || docked) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, docked]);

  const toggleSection = (id, headerCollapsed) => {
    setCollapsed(prev => {
      const next = { ...prev, [id]: !headerCollapsed };
      try { localStorage.setItem('tmr-nav-collapse', JSON.stringify(next)); } catch (_) {}
      return next;
    });
  };

  const handleNavClick = (link, e) => {
    if (!docked) onClose();
    if (!link || link === '#') {
      e.preventDefault();
    }
  };

  const sidebarClass = [
    'tmr-sidebar',
    docked ? 'tmr-sidebar-docked' : '',
    docked ? state : '',
    !docked && open ? 'open' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      {!docked && <div className={`tmr-sidebar-overlay${open ? ' open' : ''}`} onClick={onClose} />}
      <nav className={sidebarClass} id="sidebar">
        <div className="tmr-sidebar-head">
          <h3>
            <span className="tmr-brand-badge-sm"><i className="bi bi-graph-up-arrow" /></span>
            {!iconOnly && <span className="tmr-sidebar-head-title">Navigation</span>}
          </h3>
          {!docked ? (
            <button type="button" className="tmr-sidebar-close" onClick={onClose} aria-label="Close menu">
              <i className="bi bi-x-lg" />
            </button>
          ) : onHide && (
            <button
              type="button"
              className="tmr-sidebar-close tmr-sidebar-hide-btn"
              onClick={onHide}
              title="Hide panel"
              aria-label="Hide navigation panel"
            >
              <i className="bi bi-chevron-bar-left" />
            </button>
          )}
        </div>

        {docked && state !== 'hidden' && (
          <button
            type="button"
            className="tmr-sidebar-edge-toggle"
            onClick={onEdgeToggle}
            title={state === 'expanded' ? 'Collapse to icons' : 'Expand panel'}
            aria-label={state === 'expanded' ? 'Collapse navigation panel' : 'Expand navigation panel'}
          >
            <i className={`bi bi-chevron-${state === 'expanded' ? 'left' : 'right'}`} />
          </button>
        )}

        <div className="tmr-sidebar-nav">
          {sections.map((sec, idx) => {
            const secId = `navSec${idx}`;
            const hasActive = sectionHasActive(sec.items, activeKey);
            const isSingle = sec.items?.length <= 1 && !sec.collapsible;
            const isCollapsed = !iconOnly && sec.collapsible && !hasActive && collapsed[secId];

            return (
              <div key={secId}>
                {idx > 0 && <div className="tmr-section-divider" />}

                {isSingle ? (
                  <>
                    {!iconOnly && (
                      <div className="tmr-section-header">
                        <span className="tmr-section-label">{sec.section}</span>
                      </div>
                    )}
                    {sec.items.map(item => (
                      <a
                        key={item.key}
                        href={item.link}
                        className={`tmr-standalone-item${activeKey === item.key ? ' active' : ''}`}
                        onClick={(e) => handleNavClick(item.link, e)}
                        title={iconOnly ? item.label : undefined}
                      >
                        <i className={item.icon} />
                        {!iconOnly && <span>{item.label}</span>}
                      </a>
                    ))}
                  </>
                ) : (
                  <>
                    {!iconOnly && (
                      <div
                        className={`tmr-section-header${sec.collapsible ? ' collapsible' : ''}${isCollapsed ? ' collapsed' : ''}`}
                        onClick={sec.collapsible ? () => toggleSection(secId, isCollapsed) : undefined}
                        role={sec.collapsible ? 'button' : undefined}
                        aria-expanded={sec.collapsible ? String(!isCollapsed) : undefined}
                      >
                        <span className="tmr-section-label">
                          {sec.section}
                          {sec.live && (
                            <span className="tmr-live-badge"><span className="tmr-live-dot" />LIVE</span>
                          )}
                        </span>
                        {sec.collapsible && <i className="bi bi-chevron-down tmr-section-chevron" />}
                      </div>
                    )}
                    <div
                      id={secId}
                      className={`tmr-section-body${isCollapsed ? ' collapsed' : ''}${sec.live ? ' live-section' : ''}${iconOnly ? ' icon-rail' : ''}`}
                      style={!isCollapsed || iconOnly ? { maxHeight: iconOnly ? 2000 : 500 } : undefined}
                    >
                      {sec.items.map(item => (
                        <a
                          key={item.key}
                          href={item.link}
                          className={`tmr-sub-item${activeKey === item.key ? ' active' : ''}`}
                          onClick={(e) => handleNavClick(item.link, e)}
                          title={iconOnly ? `${sec.section}: ${item.label}` : undefined}
                        >
                          <i className={item.icon} />
                          {!iconOnly && <span>{item.label}</span>}
                        </a>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}

          <div className="tmr-section-divider" />
          <a
            href="/pages/logout.php"
            className="tmr-logout-item"
            title={iconOnly ? 'Logout' : undefined}
          >
            <i className="bi bi-power" />
            {!iconOnly && <span>Logout</span>}
          </a>
        </div>
      </nav>
    </>
  );
}